import { useState, useRef, useEffect } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Fade,
  IconButton,
  Paper,
  Snackbar,
  styled,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { TimelineItem as MuiTimelineItem, TimelineSeparator, TimelineConnector, TimelineContent } from '@mui/lab';
import { CitationItem, TimelineEvent, TimelineEventSummary } from './Interface';
import { TimelineTypeIconMap, TimelineTypeToSettingsKey, HealthTimelineSettingsLabel } from './Constants';
import { sanitizeNote } from 'op-pages/RO/Notes/sanitizeNote';
import * as Sentry from '@sentry/react';
import { ContentCopyOutlined, ContentCopy, CloseOutlined, FlagOutlined } from '@mui/icons-material';
import PillCitations from './PillCitations';
import {
  useCopyToClipboard,
  formatTimelineContent,
  deduplicateNoteCitations,
  removeBulletPoints,
} from '../SummaryUtils';
import NotificationSnackbar from 'shared-components/components/NotificationSnackbar';
import { FeedbackModal, useFeedbackHandler } from 'shared-components/components/AiFeedback';
import { logDatadogRumCustomAction } from 'op-utils/datadogRumCustomAction';

const Content = styled('div')<{ $format: boolean }>`
  display: inline;
  line-height: 24px;
  font-size: 14px;
  white-space: ${({ $format }: { $format: boolean }) => ($format ? 'normal' : 'pre-wrap')};
  color: ${(props) => props.theme.palette.text.primary};
  padding-top: 0px;
  padding-bottom: 8px;
  word-break: break-word;
  font,
  *,
  ul {
    margin-left: 10px !important;
  },
  ol {
    margin-left: 10px;
  },
  li {
    margin-bottom: 8px;
    list-style-type: disc;
  },
  b {
    color: ${(props) => props.theme.palette.text.primary};
    margin: 0px !important;
  },
`;

const TimelineRowContainer = ({
  citations,
  text,
  onRelatedItemClick,
  patientId,
}: {
  citations: CitationItem[];
  text: string;
  onRelatedItemClick: (item: CitationItem) => void;
  patientId?: string;
}) => {
  const [hovering, setHovering] = useState(false);
  return (
    <div
      style={{
        display: 'block',
        width: '100%',
        padding: '4px 0 4px 8px',
        borderRadius: '4px',
        transition: 'background-color 0.2s ease',
        backgroundColor: hovering ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
      }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}>
      <Content $format={true} dangerouslySetInnerHTML={sanitizeNote(removeBulletPoints(text), true)} />
      <PillCitations
        items={deduplicateNoteCitations(citations)}
        onRelatedItemClick={onRelatedItemClick}
        expanded={hovering}
        patientId={patientId}
      />
    </div>
  );
};

export const TimelineItem = ({
  event,
  index,
  onRelatedItemClick,
  contents,
  contentType,
  isSuperuser,
  patientId,
}: {
  event: TimelineEvent;
  index: number;
  isLastItem: boolean;
  onRelatedItemClick: (content: CitationItem) => void;
  contents: TimelineEventSummary[];
  contentType: string;
  onCitationGroupSelection: (selectedGroupId: number | null) => void;
  selectedCitationGroupId: number | null;
  isSuperuser: boolean;
  patientId?: string;
}): JSX.Element => {
  const [isOnHover, setIsOnHover] = useState(false);
  const theme = useTheme();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const { copySnackbarOpen, copyError, copyToClipboard, setCopySnackbarOpen } = useCopyToClipboard();
  const [copiedButton, setCopiedButton] = useState<null | 'content' | 'resource' | 'trace' | 'span'>(null);

  const clearTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (clearTimerRef.current) window.clearTimeout(clearTimerRef.current);
    };
  }, []);

  // AI Feedback hook
  const {
    feedbackModalOpen,
    feedbackSnackbarOpen,
    feedbackLoading,
    feedbackSuccess,
    feedbackErrorMessage,
    openFeedbackModal,
    closeFeedbackModal,
    submitFeedback,
    closeFeedbackSnackbar,
  } = useFeedbackHandler({
    defaultCommentPrefix: `AI Timeline feedback for ${event.title}`,
  });

  const handleCopyToClipboard = async (
    value?: string | number,
    buttonType?: 'content' | 'resource' | 'trace' | 'span',
  ) => {
    logDatadogRumCustomAction('ai_timeline_copy', {
      patientId,
      viewMode: contentType,
      key: event.type,
    });
    const textToCopy = value != null ? String(value) : formatTimelineContent(event.title, event.date, contents);

    await copyToClipboard(textToCopy);
    setCopiedButton(buttonType ?? 'content');
    if (clearTimerRef.current) window.clearTimeout(clearTimerRef.current);
    clearTimerRef.current = window.setTimeout(() => setCopiedButton(null), 2000);
  };

  const handleSubmitFeedback = async (category: string, comments: string) => {
    logDatadogRumCustomAction('ai_timeline_feedback_submit', {
      patientId,
      category,
      viewMode: contentType,
      key: event.type,
      value: comments.length > 0 ? 'has_comments' : 'no_comments',
    });
    // Prepare timeline content for feedback
    const timelineContent = {
      type: event.type,
      title: event.title,
      date: event.date,
      isLatest: event.isLatest,
      isNew: event.isNew,
      resourceId: event.resourceId,
      traceId: event.traceId,
      spanId: event.spanId,
      contents: contents?.map((content) => ({
        text: content.text,
        citations: content.citations?.map((citation) => ({
          title: citation.title,
          documentType: citation.documentType,
          dateOfDocument: citation.dateOfDocument,
        })),
        subItems: content.subItems?.map((subItem) => ({
          text: subItem.text,
          citations: subItem.citations?.map((citation) => ({
            title: citation.title,
            documentType: citation.documentType,
            dateOfDocument: citation.dateOfDocument,
          })),
        })),
      })),
    };

    await submitFeedback(category, comments, timelineContent, event.traceId, event.spanId);
  };

  return (
    <MuiTimelineItem key={index} onMouseEnter={() => setIsOnHover(true)} onMouseLeave={() => setIsOnHover(false)}>
      <TimelineSeparator>
        <Badge
          sx={{ color: theme.palette.error.main }}
          overlap="circular"
          badgeContent=" "
          invisible={!event.isNew}
          variant="dot"
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: '32px', height: '32px' }}>
            {TimelineTypeIconMap[event.type as keyof typeof TimelineTypeIconMap]}
          </Avatar>
        </Badge>
        <TimelineConnector sx={{ margin: '16px 0px' }} />
      </TimelineSeparator>
      <TimelineContent sx={{ padding: '0px 8px 16px 8px' }}>
        <Paper elevation={0} sx={{ p: 2, padding: '0px' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', paddingBottom: '5px' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '4px' }}>
                <Typography variant="subtitle1" sx={{ wordBreak: 'break-word' }}>
                  {event.title}
                </Typography>
                {event.isLatest && (
                  <Typography variant="subtitle1" color="textSecondary">
                    (Latest)
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Typography variant="body2" color="textPrimary">
                  {event.date}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {HealthTimelineSettingsLabel[
                    TimelineTypeToSettingsKey[
                      event.type as keyof typeof TimelineTypeToSettingsKey
                    ] as keyof typeof HealthTimelineSettingsLabel
                  ] || event.type}
                </Typography>
              </Box>
            </Box>
          </Box>
          <ul>
            {contents.map((content, index) => (
              <li key={index} style={{ listStyleType: 'disc', marginLeft: '16px' }}>
                <Sentry.ErrorBoundary fallback={<div>Error formatting content</div>}>
                  <TimelineRowContainer
                    citations={content.citations}
                    text={content.text}
                    onRelatedItemClick={onRelatedItemClick}
                    patientId={patientId}
                  />
                </Sentry.ErrorBoundary>
                <ul style={{ paddingLeft: '16px', marginLeft: '16px' }}>
                  {content.subItems?.map((subItem, index) => (
                    <li key={index} style={{ listStyleType: 'disc' }}>
                      <Sentry.ErrorBoundary fallback={<div>Error formatting content</div>}>
                        <TimelineRowContainer
                          citations={subItem?.citations || []}
                          text={subItem.text}
                          onRelatedItemClick={onRelatedItemClick}
                          patientId={patientId}
                        />
                      </Sentry.ErrorBoundary>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
          {isOnHover ? (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '8px', height: '48px', padding: '8px 0px' }}>
              <Tooltip title="Copy">
                <IconButton
                  size="small"
                  sx={{ width: '28px', height: '28px' }}
                  aria-label="Copy timeline event content to clipboard"
                  onClick={() => handleCopyToClipboard(undefined, 'content')}>
                  {copiedButton === 'content' ? (
                    <ContentCopy fontSize="small" />
                  ) : (
                    <ContentCopyOutlined fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              {isSuperuser && event.resourceId && (
                <Tooltip title={`Copy resource ID: ${event.resourceId}`}>
                  <IconButton
                    size="small"
                    sx={{ width: '28px', height: '28px' }}
                    aria-label={`Copy resource ID ${event.resourceId} to clipboard`}
                    onClick={() => handleCopyToClipboard(event.resourceId, 'resource')}>
                    {copiedButton === 'resource' ? (
                      <ContentCopy fontSize="small" />
                    ) : (
                      <ContentCopyOutlined fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              )}
              {isSuperuser && event.traceId && (
                <Tooltip title={`Copy trace ID: ${event.traceId}`}>
                  <IconButton
                    size="small"
                    sx={{ width: '28px', height: '28px' }}
                    aria-label={`Copy trace ID ${event.traceId} to clipboard`}
                    onClick={() => handleCopyToClipboard(event.traceId, 'trace')}>
                    {copiedButton === 'trace' ? (
                      <ContentCopy fontSize="small" />
                    ) : (
                      <ContentCopyOutlined fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              )}
              {isSuperuser && event.spanId && (
                <Tooltip title={`Copy span ID: ${event.spanId}`}>
                  <IconButton
                    size="small"
                    sx={{ width: '28px', height: '28px' }}
                    aria-label={`Copy span ID ${event.spanId} to clipboard`}
                    onClick={() => handleCopyToClipboard(event.spanId, 'span')}>
                    {copiedButton === 'span' ? (
                      <ContentCopy fontSize="small" />
                    ) : (
                      <ContentCopyOutlined fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Flag an issue">
                <IconButton
                  size="small"
                  sx={{ width: '28px', height: '28px' }}
                  aria-label="Flag an issue with this timeline event"
                  onClick={() => {
                    logDatadogRumCustomAction('ai_timeline_feedback_open', {
                      patientId,
                      viewMode: contentType,
                      key: event.type,
                    });
                    openFeedbackModal();
                  }}>
                  <FlagOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Box sx={{ height: '48px' }} />
          )}
        </Paper>
      </TimelineContent>
      <Snackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        TransitionComponent={Fade}
        message="Thanks for your feedback"
        key={'Fade'}
        autoHideDuration={2000}
        sx={{ height: '40px', '& .MuiSnackbarContent-root': { padding: '2px 16px' } }}
        action={
          <IconButton size="small" onClick={() => setSnackbarOpen(false)}>
            <CloseOutlined fontSize="small" sx={{ fill: 'white' }} />
          </IconButton>
        }
      />
      <NotificationSnackbar
        open={copySnackbarOpen}
        onClose={() => setCopySnackbarOpen(false)}
        type={copyError ? 'error' : 'copy'}
        autoHideDuration={2000}
      />
      <NotificationSnackbar
        open={feedbackSnackbarOpen}
        onClose={closeFeedbackSnackbar}
        type={feedbackSuccess ? 'feedback' : 'error'}
        message={feedbackSuccess ? undefined : feedbackErrorMessage}
      />
      <FeedbackModal
        open={feedbackModalOpen}
        onClose={closeFeedbackModal}
        onSubmit={handleSubmitFeedback}
        loading={feedbackLoading}
      />
    </MuiTimelineItem>
  );
};
